import ApiService from './api';
import api from '../hooks/useHttp';

class LoadService extends ApiService {
  constructor() {
    super('/loads');
  }

  // Get all loads with pagination and filters
  async getLoads(params = {}) {
    return await this.get('', params);
  }

  // Get load by ID
  async getLoadById(id) {
    return await this.get(`/${id}`);
  }

  // Create new load
  async createLoad(loadData) {
    return await this.create(loadData);
  }

  // Update load
  async updateLoad(id, loadData) {
    return await this.update(id, loadData);
  }

  // Delete load
  async deleteLoad(id) {
    return await this.delete(id);
  }

  // Bulk delete loads
  async bulkDeleteLoads(ids) {
    return await this.create({ ids }, '/bulk-delete');
  }

  // Get load statistics
  async getLoadStats() {
    return await this.get('/stats');
  }

  // Search loads
  async searchLoads(query, filters = {}) {
    return await this.get('/search', { q: query, ...filters });
  }

  // Update load status
  async updateLoadStatus(id, status) {
    return await this.partialUpdate(id, { status }, '/status');
  }

  // Duplicate load
  async duplicateLoad(id) {
    return await api.post(`/loads/${id}/duplicate`);
  }

  // Assign driver to load
  async assignDriver(id, driverId) {
    return await this.partialUpdate(id, { driverId }, '/assign-driver');
  }

  // Get load tracking information
  async getLoadTracking(id) {
    return await this.get(`/${id}/tracking`);
  }

  // Update load location
  async updateLoadLocation(id, locationData) {
    return await this.partialUpdate(id, locationData, '/location');
  }

  // Upload load documents
  async uploadDocuments(id, files) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('documents', file);
    });
    return await this.upload(formData, `/${id}/documents`);
  }

  // Get load documents
  async getLoadDocuments(id) {
    return await this.get(`/${id}/documents`);
  }

  // Delete load document
  async deleteLoadDocument(id, documentId) {
    return await this.delete(documentId, `/${id}/documents`);
  }

  // Export loads
  async exportLoads(format = 'csv', filters = {}) {
    return await this.get('/export', { format, ...filters });
  }

  // Get loads by driver
  async getLoadsByDriver(driverId, params = {}) {
    return await this.get(`/driver/${driverId}`, params);
  }

  // Get loads by date range
  async getLoadsByDateRange(startDate, endDate, params = {}) {
    return await this.get('/date-range', { startDate, endDate, ...params });
  }

  // Get load history (single load)
  async getLoadHistory(loadId, params = {}) {
    return await this.get(`/${loadId}/history`, params);
  }

  async getLoadHistoryList(params = {}) {
    return await this.get('/history', params);
  }

  async generateBOL(loadData, loadId = null) {
    const endpoint = loadId ? `/${loadId}/generate-bol` : '/generate-bol';
    const response = await api.post(`/loads${endpoint}`, loadData);
    const body = response?.data ?? response;
    return body?.data != null && body?.key == null ? body.data : body;
  }

  async generateRateConfirmation(loadData, loadId = null) {
    const endpoint = loadId ? `/${loadId}/generate-rate-confirmation` : '/generate-rate-confirmation';
    const response = await api.post(`/loads${endpoint}`, loadData);
    const body = response?.data ?? response;
    return body?.data != null && body?.key == null ? body.data : body;
  }

  async sendFiles(payload) {
    const { loadId, recipients, attachmentKeys } = payload;
    const body = { recipients, attachmentKeys };
    if (loadId) {
      return await api.post(`/loads/${loadId}/send-files`, body);
    }
    return await api.post('/loads/send-files', { ...body, loadData: payload.loadData });
  }
}

export default new LoadService();

